import { prettyId } from './data.utils';

/**
 * Parse the browser url query param of filter modal into an array
 * @param q
 */
export const parseFilterModalQuery = (q: any) => {
  return Object.keys(q.f).reduce((acc, column) => {
    const filter = q.f[column];
    const reducedOperators = Object.keys(filter).reduce((a, operator) => {
      if (Number.isInteger(Number(operator))) {
        return a;
      }

      if (['in', 'ni', 'bo', 'ex'].includes(operator)) {
        return [
          ...a,
          {
            id: prettyId(),
            column,
            operator,
            value:
              typeof filter[operator] === 'string'
                ? filter[operator].split(',')
                : filter[operator],
          },
        ];
      }

      return [
        ...a,
        {
          id: prettyId(),
          column,
          operator,
          value: filter[operator],
        },
      ];
    }, []);
    return [...acc, ...reducedOperators];
  }, []);
};

/**
 * Filter types whose value widget is a select rather than a text field.
 *
 * <p>One list, exported, because three places used to answer this question -- the filter modal
 * decided what to render, this module decided what to array-wrap, and a third condition decided
 * what allowed more than one value. A type added to two of the three is a filter that renders as a
 * dropdown and then loses its selection on reopen.
 */
export const SELECT_WIDGET_FILTER_TYPES = [
  'list',
  'bool',
  'existence',
  'singleSelect',
  'prefixSelect',
];

/**
 * Of those, the types that accept more than one value at once.
 *
 * <p>`prefixSelect` is here because filtering invoices to two carriers is an ordinary thing to
 * want; the backend ORs the prefixes. `singleSelect`, `bool` and `existence` are not: their
 * operators compare against exactly one value.
 */
export const MULTI_VALUE_FILTER_TYPES = ['list', 'prefixSelect'];

type FilterModalConfig = Record<string, { type?: string } | undefined>;

/**
 * parseFilterModalQuery only array-wraps a filter's value for the operators used by
 * `list`/`bool`/`existence` columns (in/ni/bo/ex). `singleSelect` columns use `eq`/`ne` —
 * the same operator ids `string`/`number` columns use for a scalar TextFieldBase value —
 * so a singleSelect value comes out as a raw string. MultiSelectBase's hydration effect
 * only runs when `defaultValue` is an array, so without this, a singleSelect filter shows
 * as unselected after a reload or a bookmarked/shared filtered URL, even though it's still
 * applied. Normalizes by the column's actual filterType (from `config`) rather than by
 * operator name, so this also covers any future filterType that renders MultiSelectBase.
 */
export const withArrayWrappedMultiSelectValues = (
  filters: ReturnType<typeof parseFilterModalQuery>,
  config: FilterModalConfig
) =>
  filters.map((filter) => {
    const filterType = config?.[filter.column]?.type;
    if (
      !filterType ||
      !SELECT_WIDGET_FILTER_TYPES.includes(filterType) ||
      Array.isArray(filter.value)
    ) {
      return filter;
    }

    // A multi-value select stores its selections as one comma-joined string, and only some
    // operators get split upstream -- `in` does, `sw` does not. Wrapped whole, "PLM-S,CIC-S"
    // matches no option and the dropdown reopens empty, so the selection looks lost.
    //
    // Split on the column's type rather than on its operator, because the same `sw` carries one
    // value on a free-text column: "Acme, Inc" is one company, not two.
    if (
      MULTI_VALUE_FILTER_TYPES.includes(filterType) &&
      typeof filter.value === 'string'
    ) {
      return {
        ...filter,
        // Blanks dropped: a trailing comma is what removing the last selection leaves, and an
        // empty option matches nothing and reads as a blank chip.
        value: filter.value
          .split(',')
          .map((entry: string) => entry.trim())
          .filter(Boolean),
      };
    }

    return { ...filter, value: [filter.value] };
  });

/**
 * Split a multi-value column's selections on the way out to the URL.
 *
 * <p>While the modal is open a multi-value select holds its selections as one comma-joined
 * string. `withArrayWrappedMultiSelectValues` splits it on the way back IN, so a reopened filter
 * shows its ticks — but nothing did so on the way OUT, and Apply sent the joined string.
 *
 * <p>That matters because `stringifyQuery` runs `encodeURIComponent` on any string-operator
 * value: "PLM-S,CIC-S" became "PLM-S%2CCIC-S", one prefix the listing matched nothing against.
 * `list` columns escaped it only because their operators, in/ni, are not string operators.
 *
 * <p>Split on the column's type, never on its operator: `sw` carries one value on a free-text
 * column, where "Acme, Inc" is one company and its comma must survive as part of the value.
 */
export const withSplitMultiSelectValues = (
  filters: readonly any[],
  config: FilterModalConfig
) =>
  filters.map((filter) => {
    const filterType = config?.[filter.column]?.type;
    if (
      !filterType ||
      !MULTI_VALUE_FILTER_TYPES.includes(filterType) ||
      typeof filter.value !== 'string'
    ) {
      return filter;
    }

    return {
      ...filter,
      // Blanks dropped, as on the way in: removing the last selection leaves a trailing comma,
      // and an empty entry would be sent as a prefix matching everything.
      value: filter.value
        .split(',')
        .map((entry: string) => entry.trim())
        .filter(Boolean),
    };
  });

interface FilterOption {
  readonly value?: string;
}

const productKeysOf = (option: FilterOption) => option.value?.split(',') ?? [];

/**
 * Map a filter's raw values back onto the options that own them.
 *
 * A filter option may stand for more than one key: the single "Prime 250" option
 * carries both P250 and P250X, so that the two products a broker cannot tell apart
 * are also one checkbox (FPX-8678). `parseFilterModalQuery` splits an `in` value on
 * commas, so `f[product][in]=P250,P250X` arrives as two separate keys, and
 * `MultiSelectBase` hydrates by exact `option.value === key`. Without this step the
 * "Prime 250" checkbox reads as unselected every time the modal is reopened on a
 * filter that is in fact applied.
 *
 * An option is selected when the filter mentions *any* of its keys, so a filter
 * saved before P250X existed (`in=P250`) still rehydrates onto "Prime 250" and
 * widens to the whole book when re-applied, rather than silently narrowing.
 *
 * Options with a single key match exactly as they did before.
 */
export const toSelectedOptionValues = (
  value: unknown,
  options: readonly FilterOption[]
) => {
  // getListOptions is async and placeholders to []; regrouping before the options
  // land would clear a checkbox that is about to hydrate.
  if (!Array.isArray(value) || options.length === 0) {
    return value;
  }

  return options
    .filter((option) =>
      productKeysOf(option).some((key) => value.includes(key))
    )
    .map((option) => option.value);
};
